import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModuleSelector from '../components/ModuleSelector';
import PurchaseModal from '../components/PurchaseModal';
import type { ModuleInfo } from '../components/ModuleSelector';
import type { GameMode } from '../hooks/useGameLogic';
import type { PurchasableModule } from '../hooks/usePurchases';

const moduleColorMap: Record<PurchasableModule, string> = {
  hard: 'bg-gradient-to-r from-violet-600 to-purple-700',
  roulette: 'bg-gradient-to-r from-green-500 to-emerald-600',
  coquin: 'bg-gradient-to-r from-red-500 to-pink-500',
};

interface ModuleSelectProps {
  onSelectMode: (mode: GameMode) => void;
  isModulePurchased: (module: PurchasableModule) => boolean;
  onPurchase: (module: PurchasableModule) => Promise<boolean>;
  onRestore: () => Promise<boolean>;
}

export default function ModuleSelect({
  onSelectMode,
  isModulePurchased,
  onPurchase,
  onRestore,
}: ModuleSelectProps) {
  const navigate = useNavigate();
  const [purchaseModal, setPurchaseModal] = useState<ModuleInfo | null>(null);
  const [restoring, setRestoring] = useState(false);

  const handleSelectModule = (mode: GameMode) => {
    onSelectMode(mode);
    navigate('/game');
  };

  const handleLockedModule = (module: ModuleInfo) => {
    setPurchaseModal(module);
  };

  const handleRestore = async () => {
    setRestoring(true);
    await onRestore();
    setRestoring(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-[#0d1117] to-black flex flex-col p-6 page-enter safe-top safe-bottom safe-x">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/players')}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 btn-press"
        >
          ←
        </button>
        <div>
          <h1 className="font-display font-bold text-2xl text-white">Mode de jeu</h1>
          <p className="font-body text-white/40 text-sm">Choisis ton ambiance</p>
        </div>
      </div>

      {/* Module cards */}
      <div className="flex-1">
        <ModuleSelector
          onSelectModule={handleSelectModule}
          onLockedModule={handleLockedModule}
          isModulePurchased={isModulePurchased}
        />
      </div>

      {/* Restore purchases */}
      <div className="mt-6 pb-4 text-center">
        <button
          onClick={handleRestore}
          disabled={restoring}
          className="text-white/30 font-body text-xs underline btn-press"
        >
          {restoring ? 'Restauration...' : 'Restaurer les achats'}
        </button>
      </div>

      {/* Purchase modal */}
      {purchaseModal && purchaseModal.purchaseId && (
        <PurchaseModal
          isOpen={!!purchaseModal}
          moduleName={purchaseModal.name}
          moduleId={purchaseModal.purchaseId}
          moduleEmoji={purchaseModal.emoji}
          moduleColor={moduleColorMap[purchaseModal.purchaseId]}
          onPurchase={onPurchase}
          onClose={() => setPurchaseModal(null)}
        />
      )}
    </div>
  );
}
